import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  AllBunkerGradeOverridesQuery,
  AllBunkerGradeOverridesQueryVariables,
  BunkerExMutation,
  BunkerExMutationVariables,
  BunkerGradeOverride_AdminFragment,
  CurrentUserQuery,
} from '../../graphql';
import {
  resolveAllBunkerGrades,
  blendFactor,
} from '../../lib/bunker-blend.utils';
import {
  AllBunkerGradeOverridesDocument,
  BunkerExDocument,
  useAllBunkerGradeOverridesQuery,
  useBunkerExMutation,
} from '../../graphql';
import type { CurrencyRates } from '../../state/zustand';
import {
  useBunkerEx,
  useSetBunkerEx,
  convertMonetaryValue,
  useCurrencyRates,
} from '../../state/zustand';
import { DefaultMap, mapKeyBy2 } from '@seaber/common/lib/collections';
import { parseCurrency } from '@seaber/common/lib/currency';
import { useNotify } from '../notifications/ToastNotifications';
import type { ApolloClient } from '@apollo/client';
import { compact } from 'lodash';

type BunkerPrice = NonNullable<
  NonNullable<NonNullable<BunkerExMutation['bunkerEx']>['prices']>[number]
>;
type ParsedBunkerGradeOverrides = ReturnType<typeof parseBunkerGradeOverrides>;
export type BunkerPriceByGradeName = (gradeName: string) => number | null;
export type UseBunkerGradeOverridesValue = ReturnType<
  typeof useBunkerGradeOverrides
>;

/** We use this from any non-react context. */
export async function fetchBunkerExPrices(
  client: ApolloClient<unknown>,
): Promise<BunkerExMutation['bunkerEx']> {
  const bunkerExMutationResult = await client.mutate<
    BunkerExMutation,
    BunkerExMutationVariables
  >({
    mutation: BunkerExDocument,
  });

  return bunkerExMutationResult.data?.bunkerEx;
}

/** We use this from any non-react context. */
export async function fetchBunkerGradeOverrides(
  client: ApolloClient<unknown>,
): Promise<AllBunkerGradeOverridesQuery | undefined> {
  const queryResult = await client.query<
    AllBunkerGradeOverridesQuery,
    AllBunkerGradeOverridesQueryVariables
  >({
    query: AllBunkerGradeOverridesDocument,
  });

  return queryResult.data;
}

export function parseBunkerEx(
  bunkerEx: BunkerExMutation['bunkerEx'],
): Map<string | null | undefined, Map<string | null | undefined, BunkerPrice>> {
  return mapKeyBy2(
    compact(bunkerEx?.prices),
    (p) => p.fuelGrade.id.toUpperCase(),
    (p) => p.port.id,
  );
}

export function parseBunkerGradeOverrides(
  data: AllBunkerGradeOverridesQuery | undefined,
) {
  const bunkerGradeOverrides: BunkerGradeOverride_AdminFragment[] = [];
  const overridesByReferenceGrade = new DefaultMap<
    string,
    BunkerGradeOverride_AdminFragment[]
  >(() => []);

  data?.allBunkerGradeOverrides?.nodes.forEach((bgo) => {
    if (!bgo) return;

    bunkerGradeOverrides.push(bgo);

    if (!bgo.referenceGrade) return;
    overridesByReferenceGrade.get(bgo.referenceGrade).push(bgo);
  });

  return {
    bunkerGradeOverrides,
    overridesByReferenceGrade,
  };
}

/** Looks up bunker grade override by grade name, or a partial match */
export function getBunkerOverrideByGrade(
  {
    bunkerGradeOverrides,
    overridesByReferenceGrade,
  }: ParsedBunkerGradeOverrides,
  gradeName: string,
): BunkerGradeOverride_AdminFragment | undefined {
  return (
    overridesByReferenceGrade.get(gradeName)?.at(0) ??
    bunkerGradeOverrides.find((bgo) => bgo.gradeName.includes(gradeName))
  );
}

const ROTTERDAM_PORT_CODE = 'NLRTM';

function resolvePriceFromBunkerExResult(
  bunkerExByGradeAndPortCode: Map<
    string | null | undefined,
    Map<string | null | undefined, BunkerPrice>
  >,
  gradeName: string,
  referenceGrade: string | null | undefined,
  referencePortCode: string,
): number {
  const getPriceFromBEX = (grade: string, port: string) =>
    bunkerExByGradeAndPortCode.get(grade)?.get(port)?.latestPrices[0]?.price;

  /**
   * resolve price with this priority:
   * 1) reference grade from defined reference port
   * 2) grade from defined reference port
   * 3) reference grade from rotterdam
   * 4) grade from rotterdam
   */

  if (referenceGrade) {
    const price = getPriceFromBEX(referenceGrade, referencePortCode);
    if (price) {
      return price;
    }
  }

  const gradePriceFromReferencePort = getPriceFromBEX(
    gradeName,
    referencePortCode,
  );
  if (gradePriceFromReferencePort) {
    return gradePriceFromReferencePort;
  }

  if (referencePortCode !== ROTTERDAM_PORT_CODE) {
    if (referenceGrade) {
      const referenceGradePriceFromRotterdam = getPriceFromBEX(
        referenceGrade,
        ROTTERDAM_PORT_CODE,
      );
      if (referenceGradePriceFromRotterdam) {
        return referenceGradePriceFromRotterdam;
      }
    }

    const gradePriceFromRotterdam = getPriceFromBEX(
      gradeName,
      ROTTERDAM_PORT_CODE,
    );
    if (gradePriceFromRotterdam) {
      return gradePriceFromRotterdam;
    }
  }

  return 0;
}

function resolveBlendPrice(
  bunkerExByGradeAndPortCode: Map<
    string | null | undefined,
    Map<string | null | undefined, BunkerPrice>
  >,
  parsedBunkerGradeOverrides: ParsedBunkerGradeOverrides,
  currencyRates: CurrencyRates | null,
  override: BunkerGradeOverride_AdminFragment,
): number | null {
  const component1 = parsedBunkerGradeOverrides.bunkerGradeOverrides.find(
    (bgo) => bgo.id === override.blendComponent1Id,
  );
  const component2 = parsedBunkerGradeOverrides.bunkerGradeOverrides.find(
    (bgo) => bgo.id === override.blendComponent2Id,
  );

  if (!component1 || !component2) return null;

  // DB trigger prevents nested blends, but guard against data corruption or race conditions
  if (component1.isBlend || component2.isBlend) {
    console.error('Blend component is itself a blend; cannot compute price.');
    return null;
  }

  const price1 = resolveBunkerPrice(
    bunkerExByGradeAndPortCode,
    parsedBunkerGradeOverrides,
    currencyRates,
    component1.gradeName,
    component1,
  );
  const price2 = resolveBunkerPrice(
    bunkerExByGradeAndPortCode,
    parsedBunkerGradeOverrides,
    currencyRates,
    component2.gradeName,
    component2,
  );

  if (price1 === null || price2 === null) return null;

  const pct1 = Number(override.blendPercentage1) / 100;
  const pct2 = Number(override.blendPercentage2) / 100;

  return blendFactor(price1, price2, pct1, pct2);
}

/** Returns the final bunker price considering the bunker ex price and bunker grade overrides.
 * Uses the reference port from the bunker grade override, or falls back to Rotterdam (NLRTM).
 */
export function resolveBunkerPrice(
  bunkerExByGradeAndPortCode: Map<
    string | null | undefined,
    Map<string | null | undefined, BunkerPrice>
  >,
  parsedBunkerGradeOverrides: ParsedBunkerGradeOverrides,
  currencyRates: CurrencyRates | null,
  gradeName: string,
  knownOverride?: BunkerGradeOverride_AdminFragment,
): number | null {
  const overrideGrade =
    knownOverride ??
    getBunkerOverrideByGrade(parsedBunkerGradeOverrides, gradeName);

  if (overrideGrade?.isBlend) {
    return resolveBlendPrice(
      bunkerExByGradeAndPortCode,
      parsedBunkerGradeOverrides,
      currencyRates,
      overrideGrade,
    );
  }

  const priceAdjustment = +(overrideGrade?.priceAdjustment ?? 0);

  // Use the reference port from the override, or fall back to Rotterdam
  const referencePortCode = overrideGrade?.referencePort ?? ROTTERDAM_PORT_CODE;

  const price =
    overrideGrade?.price ??
    resolvePriceFromBunkerExResult(
      bunkerExByGradeAndPortCode,
      gradeName,
      overrideGrade?.referenceGrade,
      referencePortCode,
    ) + priceAdjustment;

  return price
    ? (convertMonetaryValue(
        +price,
        parseCurrency(overrideGrade?.currency ?? 'USD'),
        currencyRates,
      ) ?? +price)
    : null;
}

/** Returns a simplified bunker price map for all bunker grades, using each grade's reference port */
export function precomputeBunkerPrices(
  bunkerExByGradeAndPortCode: Map<
    string | null | undefined,
    Map<string | null | undefined, BunkerPrice>
  >,
  parsedBunkerGradeOverrides: ParsedBunkerGradeOverrides,
  currencyRates: CurrencyRates | null,
): Map<string, number | null> {
  const allBunkerGrades = new Set<string>(
    compact([
      ...bunkerExByGradeAndPortCode.keys(),
      ...parsedBunkerGradeOverrides.bunkerGradeOverrides.map(
        (bgo) => bgo.gradeName,
      ),
    ]),
  );

  const bunkerPriceByGradeName = new Map<string, number | null>(
    Array.from(allBunkerGrades).map((gradeName) => [
      gradeName,
      resolveBunkerPrice(
        bunkerExByGradeAndPortCode,
        parsedBunkerGradeOverrides,
        currencyRates,
        gradeName,
      ),
    ]),
  );
  return bunkerPriceByGradeName;
}

export function useBunkerGradeOverrides() {
  const { data } = useAllBunkerGradeOverridesQuery();

  const parsedBunkerGradeOverrides = useMemo(
    () => parseBunkerGradeOverrides(data),
    [data],
  );

  const getOverrideByGrade = useCallback(
    (gradeName: string) =>
      getBunkerOverrideByGrade(parsedBunkerGradeOverrides, gradeName),
    [parsedBunkerGradeOverrides],
  );

  return {
    parsedBunkerGradeOverrides,
    getOverrideByGrade,
  };
}

export function useBunkerPrices() {
  const { parsedBunkerGradeOverrides } = useBunkerGradeOverrides();
  const bunkerExResult = useBunkerEx();
  const bunkerExByGradeAndPortCode = useMemo(
    () => parseBunkerEx(bunkerExResult),
    [bunkerExResult],
  );
  const currencyRates = useCurrencyRates();

  const bunkerPriceByGradeName = useCallback(
    (gradeName: string) =>
      resolveBunkerPrice(
        bunkerExByGradeAndPortCode,
        parsedBunkerGradeOverrides,
        currencyRates,
        gradeName,
      ),
    [bunkerExByGradeAndPortCode, parsedBunkerGradeOverrides, currencyRates],
  );

  const resolvedBunkerGrades = useMemo(
    () =>
      resolveAllBunkerGrades(
        parsedBunkerGradeOverrides.bunkerGradeOverrides,
        (gradeName) =>
          resolveBunkerPrice(
            bunkerExByGradeAndPortCode,
            parsedBunkerGradeOverrides,
            currencyRates,
            gradeName,
          ),
      ),
    [bunkerExByGradeAndPortCode, parsedBunkerGradeOverrides, currencyRates],
  );

  return {
    bunkerPriceByGradeName,
    resolvedBunkerGrades,
  };
}

export function useFetchBunkerExPrices(
  currentUser?: CurrentUserQuery['currentUser'],
): boolean {
  const skip = !currentUser;
  const [dataReady, setDataReady] = useState(false);
  const [bunkerExMutation] = useBunkerExMutation();
  const setBunkerEx = useSetBunkerEx();

  const notify = useNotify({
    toastId: 'bunkerEx',
  });

  useEffect(() => {
    if (skip) return;

    let isMounted = true;

    const fetchBunkerPrices = async () => {
      try {
        const result = await bunkerExMutation();

        if (!isMounted) return;

        const bunkerData = result.data?.bunkerEx;

        if (bunkerData?.message) {
          notify(`Failed to fetch bunker prices: ${bunkerData.message}`, {
            autoClose: false,
            type: 'error',
          });
          console.error('Failed to fetch bunker prices:', bunkerData.message);
        } else if (bunkerData) {
          setBunkerEx(bunkerData);
        }
      } catch (error) {
        if (!isMounted) return;

        const errorMessage =
          error instanceof Error ? error.message : String(error);
        notify(`Failed to fetch bunker prices: ${errorMessage}`, {
          autoClose: false,
          type: 'error',
        });
        console.error('Failed to fetch bunker prices:', error);
      } finally {
        if (isMounted) {
          setDataReady(true);
        }
      }
    };

    fetchBunkerPrices();

    return () => {
      isMounted = false;
    };
  }, [bunkerExMutation, skip, notify, setBunkerEx]);

  return dataReady;
}
